import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useLeague } from '../hooks/useLeague';
import {
  getLscVodCandidate,
  listLscVodCandidateMatches,
  matchLscVodCandidate,
  LscApiError,
} from '../api';
import type { LscVodCandidate, LscVodMatchSuggestion } from '../types';
import CandidateMatchSummary from '../components/CandidateMatchSummary';
import ConfidenceBadge from '../components/ConfidenceBadge';
import ReasonCodePills from '../components/ReasonCodePills';
import ReviewActionPanel from '../components/ReviewActionPanel';
import TruthVerificationPanel from '../components/TruthVerificationPanel';
import VodReviewNextAction from '../components/VodReviewNextAction';
import VodReviewProgress from '../components/VodReviewProgress';
import CandidateWatchVodSection from '../components/CandidateWatchVodSection';
import ReviewStatusBadge from '../components/ReviewStatusBadge';
import IgnoredCandidateBanner from '../components/IgnoredCandidateBanner';
import LoadingState from '../components/LoadingState';
import ErrorBanner from '../components/ErrorBanner';
import CommissionerOnlyGate from '../components/CommissionerOnlyGate';
import { lscPath, lscReturnTo } from '../routing';
import { parseLscMutationError } from '../entitlementBillingErrors';
import {
  getVerifiedHandoffProvenance,
  matcherUnlikelyWarning,
  METADATA_MATCHER_BUTTON_LABEL,
  METADATA_MATCHER_HELPER_COPY,
  METADATA_MATCHER_RUNNING_LABEL,
  METADATA_MATCHER_UNVERIFIED_WARNING,
  candidatePreviouslyUnpublished,
  formatSuggestionOptionLabel,
  PREVIOUSLY_UNPUBLISHED_BANNER_BODY,
  PREVIOUSLY_UNPUBLISHED_BANNER_TITLE,
  truthReviewActionsLocked,
  vodReviewScheduleMatchSelectionVisible,
} from '../vodTruth';
import {
  MATCH_STRENGTH_LABEL,
  SCHEDULED_GAME_LABEL,
  WHY_THIS_MATCHED_HELPER,
  WHY_THIS_MATCHED_LABEL,
  IGNORED_CANDIDATE_BACKFILL_LINK,
  IGNORED_CANDIDATE_STATUS_LABEL,
} from '../commissionerReviewCopy';
import { isIgnoredVodCandidate } from '../archiveBackfillTruth';

const LscCandidateDetailPage: React.FC = () => {
  const { leagueId, candidateId: rawCandidateId } = useParams<{
    leagueId: string;
    candidateId: string;
  }>();
  const candidateId = rawCandidateId ? decodeURIComponent(rawCandidateId) : '';
  const returnTo = lscReturnTo(
    `/leagues/${leagueId}/vods/${encodeURIComponent(candidateId)}`
  );

  return (
    <LscAuthGate returnTo={returnTo}>
      <CommissionerOnlyGate>
        <CandidateDetailContent candidateId={candidateId} />
      </CommissionerOnlyGate>
    </LscAuthGate>
  );
};

function CandidateDetailContent({ candidateId }: { candidateId: string }) {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { isCommissioner } = useLeague();
  const [candidate, setCandidate] = useState<LscVodCandidate | null>(null);
  const [suggestions, setSuggestions] = useState<LscVodMatchSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!leagueId || !candidateId) return;
    setLoading(true);
    setError(null);
    try {
      const [cand, sug] = await Promise.all([
        getLscVodCandidate(leagueId, candidateId),
        listLscVodCandidateMatches(leagueId, candidateId, { include_low_confidence: true }),
      ]);
      setCandidate(cand);
      setSuggestions(sug);
    } catch (err) {
      if (err instanceof LscApiError && err.status === 403) {
        setError('Only league commissioners can sync and review VOD candidates right now.');
      } else {
        setError(parseLscMutationError(err, 'Could not load candidate.').message);
      }
    } finally {
      setLoading(false);
    }
  }, [leagueId, candidateId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRunMatcher = async () => {
    if (!leagueId || !candidateId) return;
    setMatching(true);
    setError(null);
    try {
      await matchLscVodCandidate(leagueId, candidateId, { dry_run: false });
      await load();
    } catch (err) {
      const mapped = parseLscMutationError(
        err,
        "Couldn't match this VOD to a league game. Check that the schedule is imported, then try again."
      );
      const code = err instanceof LscApiError ? err.code : undefined;
      if (
        code &&
        (code.startsWith('entitlement_') ||
          code === 'not_found' ||
          code === 'league_archived' ||
          code === 'vod_sync_apply_not_eligible' ||
          code === 'vod_sync_apply_not_in_canary')
      ) {
        setError(mapped.message);
      } else {
        setError(
          "Couldn't match this VOD to a league game. Check that the schedule is imported, then try again."
        );
      }
    } finally {
      setMatching(false);
    }
  };

  if (loading) return <LoadingState />;

  if (!candidate) {
    return error ? <ErrorBanner message={error} /> : null;
  }

  const bestSuggestion = suggestions[0] ?? null;
  const isIgnored = isIgnoredVodCandidate(candidate);
  const matcherWarning = !isIgnored ? matcherUnlikelyWarning(candidate) : null;
  const reviewLocked = truthReviewActionsLocked(candidate);
  const previouslyUnpublished = !isIgnored && candidatePreviouslyUnpublished(candidate);
  const scheduleMatchInVerification = vodReviewScheduleMatchSelectionVisible(candidate);
  const handoffProvenance = getVerifiedHandoffProvenance(candidate);

  return (
    <div className="lsc-split-when-wide space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          {isIgnored ? (
            <Link
              to={lscPath(`/leagues/${leagueId}/review?tab=backfill`)}
              className="text-sm text-primary-300 hover:text-primary-200"
              data-testid="ignored-candidate-detail-back-link"
            >
              ← {IGNORED_CANDIDATE_BACKFILL_LINK}
            </Link>
          ) : (
            <Link
              to={lscPath(`/leagues/${leagueId}/vods`)}
              className="text-sm text-primary-300 hover:text-primary-200"
            >
              ← Back to VOD candidates
            </Link>
          )}
          <h1 className="text-2xl font-bold text-white mt-2">{candidate.title ?? 'VOD candidate'}</h1>
          <div className="mt-2">
            {isIgnored ? (
              <span
                className="inline-flex rounded-full border px-2 py-0.5 text-xs font-medium bg-secondary-700 text-secondary-200 border-secondary-600"
                data-testid="ignored-candidate-status-badge"
              >
                {IGNORED_CANDIDATE_STATUS_LABEL}
              </span>
            ) : (
              <ReviewStatusBadge status={candidate.review_status} />
            )}
          </div>
        </div>
      </div>

      {isIgnored && <IgnoredCandidateBanner candidate={candidate} leagueId={leagueId!} />}

      {previouslyUnpublished && (
        <div
          className="rounded-lg border border-amber-700 bg-amber-900/30 p-4 text-sm"
          data-testid="previously-unpublished-banner"
        >
          <p className="font-medium text-amber-100 mb-1">{PREVIOUSLY_UNPUBLISHED_BANNER_TITLE}</p>
          <p className="text-amber-200">{PREVIOUSLY_UNPUBLISHED_BANNER_BODY}</p>
        </div>
      )}

      {matcherWarning && (
        <p className="text-sm text-amber-200/90" data-testid="matcher-unlikely-warning">
          {matcherWarning}
        </p>
      )}

      {handoffProvenance.isVerifiedLiveHandoff && (
        <div
          className="rounded-lg border border-primary-700/60 bg-primary-900/20 p-4 text-sm space-y-2"
          data-testid="verified-handoff-provenance-banner"
        >
          {handoffProvenance.bannerTitle && (
            <p className="font-medium text-primary-100">{handoffProvenance.bannerTitle}</p>
          )}
          {handoffProvenance.bannerHelper && (
            <p className="text-primary-100/90">{handoffProvenance.bannerHelper}</p>
          )}
          <dl className="grid gap-1 text-primary-100/90">
            {handoffProvenance.liveStreamId && (
              <div>
                <dt className="inline font-medium">Live stream ID: </dt>
                <dd className="inline">{handoffProvenance.liveStreamId}</dd>
              </div>
            )}
            {handoffProvenance.twitchVodId && (
              <div>
                <dt className="inline font-medium">Twitch VOD: </dt>
                <dd className="inline">{handoffProvenance.twitchVodId}</dd>
              </div>
            )}
            {handoffProvenance.matchLabel && (
              <div>
                <dt className="inline font-medium">{handoffProvenance.matchLabelPrefix}: </dt>
                <dd className="inline">{handoffProvenance.matchLabel}</dd>
              </div>
            )}
          </dl>
          {handoffProvenance.publishBlockedNote && (
            <p className="text-primary-100/80">{handoffProvenance.publishBlockedNote}</p>
          )}
        </div>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {!isIgnored && (
        <>
          <VodReviewNextAction candidate={candidate} suggestions={suggestions} />
          <VodReviewProgress candidate={candidate} suggestions={suggestions} />
        </>
      )}

      <CandidateWatchVodSection candidate={candidate} />

      <div className="max-w-lsc-narrow space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-white">VOD metadata</h2>
          {candidate.thumbnail_url && (
            <img
              src={candidate.thumbnail_url}
              alt=""
              className="rounded-lg max-h-40 object-cover"
            />
          )}
          <dl className="grid gap-2 text-sm">
            <div>
              <dt className="text-secondary-500">Broadcaster</dt>
              <dd className="text-secondary-200">
                {candidate.provider_broadcaster_login
                  ? `@${candidate.provider_broadcaster_login}`
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">Created</dt>
              <dd className="text-secondary-200">{candidate.created_at ?? '—'}</dd>
            </div>
            {isIgnored && (
              <>
                <div>
                  <dt className="text-secondary-500">Candidate status</dt>
                  <dd className="text-secondary-200">{IGNORED_CANDIDATE_STATUS_LABEL}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Sync source</dt>
                  <dd className="text-secondary-200">{candidate.sync_source ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Provider VOD ID</dt>
                  <dd className="text-secondary-200">{candidate.provider_vod_id ?? '—'}</dd>
                </div>
              </>
            )}
            {handoffProvenance.isVerifiedLiveHandoff && (
              <>
                {handoffProvenance.twitchVodId && (
                  <div>
                    <dt className="text-secondary-500">Twitch VOD ID</dt>
                    <dd className="text-secondary-200">{handoffProvenance.twitchVodId}</dd>
                  </div>
                )}
                {handoffProvenance.liveStreamId && (
                  <div>
                    <dt className="text-secondary-500">Live stream ID</dt>
                    <dd className="text-secondary-200">{handoffProvenance.liveStreamId}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-secondary-500">Source</dt>
                  <dd className="text-secondary-200">{handoffProvenance.syncSourceLabel}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Match source</dt>
                  <dd className="text-secondary-200">{handoffProvenance.matchSourceLabel}</dd>
                </div>
              </>
            )}
            <div>
              <dt className="text-secondary-500">Description</dt>
              <dd className="text-secondary-300 whitespace-pre-wrap break-words">
                {candidate.description || '—'}
              </dd>
            </div>
          </dl>
          {candidate.url && (
            <a
              href={candidate.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-300 hover:text-primary-200 text-sm"
            >
              Open on Twitch
            </a>
          )}
        </div>

        <CandidateMatchSummary candidate={candidate} bestSuggestion={bestSuggestion} />
      </div>

      {!isIgnored && (
        <>
          <TruthVerificationPanel
            leagueId={leagueId!}
            candidate={candidate}
            onUpdated={load}
          />

          <ReviewActionPanel
            leagueId={leagueId!}
            candidate={candidate}
            suggestions={suggestions}
            onUpdated={load}
            isCommissioner={isCommissioner}
          />

          <div className="card space-y-3" data-testid="metadata-matcher-table">
            <h2 className="text-lg font-semibold text-white">Match from title & stream info</h2>
            <p className="text-sm text-secondary-400">{METADATA_MATCHER_HELPER_COPY}</p>
            {reviewLocked && (
              <p className="text-sm text-amber-200/90" data-testid="metadata-matcher-unverified-warning">
                {METADATA_MATCHER_UNVERIFIED_WARNING}
              </p>
            )}
            <div
              id={scheduleMatchInVerification ? undefined : 'match-selection'}
              data-testid="metadata-match-suggestions"
            >
              <h3 className="text-sm font-medium text-white mb-3">Why this matched — or didn’t</h3>
              {suggestions.length === 0 ? (
                <p className="text-secondary-400 text-sm">
                  No suggestions yet. Run match from title & stream info after game verification, or
                  use schedule binding from the verification panel.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-secondary-400">
                      <tr>
                        <th className="text-left py-2 pr-4">{SCHEDULED_GAME_LABEL}</th>
                        <th className="text-left py-2 pr-4">{MATCH_STRENGTH_LABEL}</th>
                        <th className="text-left py-2 pr-4">Side</th>
                        <th className="text-left py-2">
                          <span>{WHY_THIS_MATCHED_LABEL}</span>
                          <span className="block text-xs font-normal text-secondary-500 mt-0.5">
                            {WHY_THIS_MATCHED_HELPER}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-700">
                      {suggestions.map((s) => (
                        <tr key={s.id}>
                          <td
                            className="py-3 pr-4 text-secondary-200"
                            title={s.competition_match_id ? `Match ID ${s.competition_match_id}` : undefined}
                          >
                            <div className="font-medium text-white">
                              {formatSuggestionOptionLabel(s)}
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            <ConfidenceBadge band={s.confidence_band} score={s.score} />
                          </td>
                          <td className="py-3 pr-4 capitalize text-secondary-300">
                            {s.matched_side ?? '—'}
                          </td>
                          <td className="py-3">
                            <ReasonCodePills
                              codes={[
                                ...(s.reason_codes ?? []),
                                ...(s.hard_reject_reasons ?? []),
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleRunMatcher}
              disabled={matching}
              data-testid="metadata-matcher-button"
            >
              {matching ? METADATA_MATCHER_RUNNING_LABEL : METADATA_MATCHER_BUTTON_LABEL}
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  );
}

export default LscCandidateDetailPage;
